import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, Mail, Lock, LogOut, User } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { GlobalSearch } from '../GlobalSearch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { fmtDateTime, ROLE_LABELS } from '../../lib/utils';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { AawtaLogo } from '../AawtaLogo';
import ChangePasswordDialog from '../ChangePasswordDialog';
import { toast } from 'sonner';

export function Topbar({ title, subtitle, onMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [pwdOpen, setPwdOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/notifications');
      setNotifs(r.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post('/notifications/read-all');
    load();
  };

  const initials = (user?.full_name || 'AA')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="topbar">
      <div className="px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onMenu} className="md:hidden p-2 -ml-2 text-navy" data-testid="topbar-menu">
            <Menu className="w-5 h-5" />
          </button>
          <div className="md:hidden">
            <AawtaLogo size={32} showText={false} />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-xl md:text-2xl text-navy truncate" data-testid="page-title">
              {title}
            </h1>
            {subtitle && <p className="text-xs md:text-sm text-slate-500 truncate">{subtitle}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <GlobalSearch />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="relative p-2 rounded-lg hover:bg-slate-100 transition"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 text-navy" />
                {unread > 0 && (
                  <span
                    className="absolute top-1 right-1 bg-magenta text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                    data-testid="notification-badge"
                  >
                    {unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                {unread > 0 && (
                  <button
                    onClick={markAll}
                    className="text-xs text-magenta font-semibold hover:underline"
                    data-testid="mark-all-read"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <DropdownMenuSeparator />
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications</div>
                )}
                {notifs.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    className={`flex flex-col items-start gap-1 py-2.5 ${n.link ? 'cursor-pointer' : 'cursor-default'}`}
                    data-testid={`notif-${n.id}`}
                    onClick={async () => {
                      if (!n.read) {
                        try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
                        load();
                      }
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className={`w-2 h-2 rounded-full ${n.read ? 'bg-slate-300' : 'bg-magenta'}`} />
                      <span className="font-semibold text-sm text-navy flex-1">{n.title}</span>
                    </div>
                    <span className="text-xs text-slate-600">{n.message}</span>
                    <span className="text-[10px] text-slate-400">{fmtDateTime(n.created_at)}</span>
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="justify-center text-xs font-semibold text-magenta cursor-pointer"
                onClick={() => navigate('/notifications')}
                data-testid="view-all-notifications"
              >
                View all notifications
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-menu">
                <Avatar className="w-9 h-9 bg-navy text-white">
                  <AvatarFallback className="bg-navy text-white text-sm font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.full_name}</div>
                <div className="text-xs text-slate-500 font-normal">{ROLE_LABELS[user?.role]}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const r = await api.post('/digest/send-me-now');
                    if (r.data.sent) toast.success(`Digest sent to ${r.data.to}`);
                    else toast.info(r.data.reason || 'Nothing to digest');
                  } catch {
                    toast.error('Could not send digest');
                  }
                }}
                data-testid="preview-digest"
                className="cursor-pointer"
              >
                <Mail className="w-4 h-4 mr-2" /> Preview my digest
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPwdOpen(true)} className="cursor-pointer" data-testid="topbar-change-password">
                <Lock className="w-4 h-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <a href="/account" data-testid="topbar-account"><User className="w-4 h-4 mr-2" /> Account & preferences</a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>{user?.email}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { logout(); window.location.href = '/login'; }} className="cursor-pointer text-rose-600" data-testid="topbar-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </header>
  );
}
